import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQueue } from '../contexts/QueueContext';

/**
 * BottomNavigation - Persistente Mobile-Navigation
 * 
 * Features:
 * - 3 Haupt-Tabs: Projekte, Checkliste, Sync
 * - Touch-Zone 64px (Daumen-erreichbar)
 * - Safe-Area Padding für iOS/Android
 * - Aktiver Tab visuell hervorgehoben
 * - Badges für Queue-Count und offene Pflicht-Dokumente
 * - Smooth Transitions (150ms)
 */

interface NavigationTab {
  id: string;
  path: string;
  icon: JSX.Element;
  label: string;
  badge?: number;
  testId: string;
}

const BottomNavigation: React.FC = () => {
  const location = useLocation();
  const { syncStatus } = useQueue();

  // Die allgemeine App-Rolle enthält keine Lagerfreigabe. Auch Monteure dürfen
  // Lagerrechte haben; maßgeblich bleibt die serverseitige Freigabeliste.
  const tabs: NavigationTab[] = [
    {
      id: 'warehouse',
      path: '/lager',
      label: 'Lager',
      testId: 'nav-tab-warehouse',
      icon: <span aria-hidden="true">▤</span>,
    },
    {
      id: 'projects',
      path: '/projects',
      label: 'Projekte',
      testId: 'nav-tab-projects',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      )
    },
    {
      id: 'sync',
      path: '/sync',
      label: 'Sync',
      badge: syncStatus.pending > 0 ? syncStatus.pending : undefined,
      testId: 'nav-tab-sync',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      )
    }
  ];

  // Prüfen, ob der aktuelle Pfad zu einem Tab gehört
  const isActive = (path: string): boolean => {
    // Exakte Übereinstimmung oder Startmatch für Unterpfade
    return location.pathname === path || 
           (path !== '/projects' && location.pathname.startsWith(path));
  };

  return (
    <nav 
      className="fixed bottom-0 inset-x-0 z-50 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 shadow-lg pb-[env(safe-area-inset-bottom)]"
      data-testid="bottom-navigation"
    >
      <div className="max-w-screen-xl mx-auto">
        <div className="grid grid-cols-3 h-16">
          {tabs.map((tab) => {
            const active = isActive(tab.path);
            
            return (
              <Link
                key={tab.id}
                to={tab.path}
                data-testid={tab.testId}
                className={`
                  flex flex-col items-center justify-center
                  transition-all duration-150
                  ${active 
                    ? 'text-blue-600 dark:text-blue-400' 
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }
                  active:scale-95
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset
                `}
              >
                {/* Icon Container mit Badge */}
                <div className="relative">
                  <div className={`
                    transition-transform duration-150
                    ${active ? 'scale-110' : 'scale-100'}
                  `}>
                    {tab.icon}
                  </div>
                  
                  {/* Badge für Queue-Count / Tasks */}
                  {tab.badge !== undefined && tab.badge > 0 && (
                    <span 
                      className="absolute -top-1 -right-2 min-w-[18px] h-[18px] flex items-center justify-center text-xs font-bold text-white bg-red-600 rounded-full px-1"
                      data-testid={`${tab.testId}-badge`}
                    >
                      {tab.badge > 99 ? '99+' : tab.badge}
                    </span>
                  )}
                </div>
                
                {/* Label */}
                <span className={`
                  text-xs mt-1 font-medium
                  ${active ? 'font-semibold' : ''}
                `}>
                  {tab.label}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default BottomNavigation;
